import { isReactive, markRaw, ref, toValue, watch } from 'vue'
import { storeToRefs } from 'pinia'
import qs from 'query-string'
import { isPlainObject } from 'lodash-es'

import type { Ref } from 'vue'

export interface UseApiConfig {
  url: string | Ref
  backend?: string
  method?: string
  data?: any
  model?: Ref
  headers?: Headers
  transform?: Function
  pick?: Function
  query?: Object | Ref
  watch?: Array<Ref> | Ref
  upload?: Boolean
  immediate?: Boolean
  before?: Function
  after?: Function
  credentials?: boolean
  /** Форсит чтение тела для статусов из httpStatusesNoBody (201/202/204) — не всегда они реально пустые. */
  hasBody?: boolean
}
type RefreshFunction = (a0?: UseApiConfig) => Promise<void>
type ApiResourceFactory = (a0: string | null) => ReturnType<RefreshFunction>
interface UseApiResult {
  model: Ref
  error: Ref
  pending: Ref<boolean>
  status: Ref<number | null>
  abort: () => void
  refresh: RefreshFunction
}

type Uri2Entity = (uri: string, re: RegExp) => string
type MapUri2Entity = Map<RegExp, Uri2Entity>

interface DefaultConfig {
  url_backend?: string
  url_sso?: string
  url_catalog?: string
  url_docparse?: string
  url_r7?: string
  store_user?: Function
  store_lock?: Function
  service?: string
  // i18n-резолвер message; без него — error_description как раньше
  resolveErrorMessage?: Function
  // Текущий язык UI для заголовка X-Locale; без хука заголовок не шлётся
  locale?: () => string | undefined
}

export const defaultConfig: DefaultConfig = {}
export function setDefaultConfig(cfg: DefaultConfig) {
  Object.assign(defaultConfig, cfg)
}

export const isApiOk = ref(true)

const httpStatusesNoBody = [201, 202, 204]
const mapUri2Entity: MapUri2Entity = new Map([
  [
    /^catalog\/(.+)\/(\d+)$/i,
    (uri, re) => {
      const r = re.exec(uri)

      if (!r) {
        return uri
      }
      const [, m, i] = r
      return `${m}/${i}`
    },
  ],
  [
    /^stock\/(.+)\/(\d+)$/i,
    (uri, re) => {
      const r = re.exec(uri)
      if (!r) {
        return uri
      }
      const [, m, i] = r
      return `${m}_stock/${i}`
    },
  ],
  [
    /^coordinator\/(.+)\/(\d+)$/i,
    (uri, re) => {
      const r = re.exec(uri)
      if (!r) {
        return uri
      }
      const [, m, i] = r
      return `${m}/${i}`
    },
  ],
])

function useUpload(url: string, files: File[]): Promise<Response> {
  const _url = `${defaultConfig.url_backend}${url}`
  const fd = new FormData()
  let i = 0
  for (let f of files) {
    fd.append(`file_${i}`, f)
    i++
  }
  return fetch(_url, {
    credentials: 'include',
    cache: 'no-cache',
    method: 'post',
    body: fd,
  })
}

export const downloadLink = (file: { url: string }): string =>
  `${defaultConfig.url_backend}download/${file.url}`

const useApi = (cfg: UseApiConfig): UseApiResult => {
  const initialCfg = { ...cfg }
  const model = initialCfg.model ? initialCfg.model : ref(initialCfg.data || null)
  const immediate = initialCfg.immediate === true || false
  const transform = initialCfg.transform ? initialCfg.transform : d => d
  const pick = initialCfg.pick ? initialCfg.pick : d => d
  const isUpload = initialCfg.upload ?? false
  const watchSources = initialCfg.watch ? initialCfg.watch : []
  const error = ref(null)
  const pending = ref(false)
  const ac = ref<AbortController | null>(null)
  const status = ref<number | null>(null)
  const { user, isAuthorized } = storeToRefs(defaultConfig.store_user?.())
  const { lockKeys: lkk } = storeToRefs(defaultConfig.store_lock?.())

  if (initialCfg.data) {
    delete initialCfg.data
  }

  if (watchSources && watch.length) {
    watch(watchSources, () => refresh(), { deep: true })
  }

  if (immediate) {
    refresh()
  }

  async function refresh(_cfg?: UseApiConfig) {
    status.value = null
    let cfg = { ...initialCfg }

    if (_cfg) {
      Object.assign(cfg, _cfg)
    }

    cfg.url = toValue(cfg.url) as string

    if (!cfg.method) {
      cfg.method = 'get'
    }
    cfg.method = String(cfg.method).toLowerCase()

    if (cfg.before instanceof Function && cfg.before() === false) {
      return
    }

    let url = URL.parse(cfg.url)
    // upload:true без entity-url → docparser /upload (Structure/finder).
    // С url вида sites/1/upload → multipart в backend (FK, архив, версии).
    const entityUploadPath =
      isUpload && cfg.url && String(cfg.url).includes('/')
        ? String(cfg.url)
        : null

    if (isUpload && !entityUploadPath) {
      url = new URL('upload', defaultConfig.url_docparse)
    } else {
      const baseurl = determineBaseUrl(cfg, model)
      url = new URL(cfg.url || 'upload', baseurl)
    }
    let query = toValue(cfg?.query)
    if (cfg.query) {
      url.search = qs.stringify(query)

      if (query.filter) {
        const params = new URLSearchParams(url.search)
        const queryParts: string[] = []

        params.forEach((value, key) => {
          const values = value.split('&&')

          values.forEach(val => {
            queryParts.push(`${key}=${encodeURIComponent(val)}`)
          })
        })
        url.search = `?${queryParts.join('&')}`
      }
    }

    let requestBody = null
    let responseBody: any = null
    let response
    let responseType
    let noBody = false

    const headers = new Headers({
      Accept: 'application/json',
    })

    if (isUpload) {
      requestBody = cfg.data
      if (!entityUploadPath) {
        requestBody.append('service', defaultConfig.service)
        requestBody.append('path', cfg.data.get('file').name)
      }
      cfg.method = 'post'
    } else if (cfg.data !== undefined && cfg.data !== null) {
      requestBody = cfg.data
    } else if (model.value !== null && ['post', 'put'].includes(cfg.method)) {
      requestBody = toValue(model)
    }

    if (!isUpload && requestBody) {
      requestBody = JSON.stringify(transform(requestBody))
    }

    /*
     !lock may use query to create header
    */
    const lk = getLockKey(url, lkk)
    if (lk) {
      headers.set('X-Api-Lock', lk)
    }

    if (isAuthorized.value) {
      headers.set('Authorization', `Bearer ${user.value.access_token}`)
    }

    // Читаем на момент запроса: после смены языка следующий запрос уже с новым
    const locale = defaultConfig.locale?.()
    if (locale) {
      headers.set('X-Locale', locale)
    }

    // Content-Type только при теле: GET с application/json без body
    // Werkzeug/Flask отвечает 400 Bad Request (превью пароля ломалось).
    if (!isUpload && requestBody) {
      headers.set('Content-Type', 'application/json')
    }

    if (cfg.headers) {
      Object.assign(headers, cfg.headers)
    }

    // Отменить предыдущий in-flight: иначе поздний ответ (напр. LTMS)
    // перезапишет модель после более нового запроса (Досье).
    if (ac.value) {
      ac.value.abort()
      ac.value = null
    }

    const myAc = new AbortController()
    ac.value = myAc
    pending.value = true

    const fconfig: RequestInit = {
      headers,
      method: cfg.method,
      cache: 'no-cache',
      body: requestBody,
      signal: myAc.signal,
    }

    if (cfg.credentials !== false) {
      fconfig.credentials = 'include'
    }

    try {
      response = await fetch(url, fconfig)
      status.value = response.status
      noBody = !cfg.hasBody && httpStatusesNoBody.includes(response.status)

      // костыль, чтобы выкидывать юзера если current_user отдал 401
      if (!response.ok && response.status === 401 && url.pathname.includes('current_user')) {
        user.value = null
      }

      if (!noBody) {
        responseType = response.headers?.get('Content-Type')?.startsWith('application/json')
          ? 'json'
          : 'bytes'

        if (responseType == 'json') {
          responseBody = await response.json()
        } else {
          // markRaw: иначе Vue deep-ref делает Proxy из Uint8Array —
          // PDF.js / blob: URL ломаются (пустой просмотрщик).
          const bytes = await response.bytes()
          responseBody = {
            bytes: markRaw(bytes),
          }
        }
      }
    } catch (e) {
      // Сменили фильтр / ушли со страницы — не трогаем model и не кидаем в UI.
      if (myAc.signal.aborted || (e as Error)?.name === 'AbortError') {
        return
      }
      throw e
    } finally {
      if (ac.value === myAc) {
        ac.value = null
        pending.value = false
      }
    }

    if (myAc.signal.aborted) {
      return
    }

    isApiOk.value = true

    if (!response.ok) {
      // message / error_description / error — в т.ч. preview_password_required
      error.value =
        responseBody?.response?.data ||
        responseBody?.message ||
        responseBody?.error_description ||
        responseBody?.error ||
        null
      if (![400, 401, 422].includes(response.status)) {
        isApiOk.value = false
      }

      // разбор errors[] + message (i18n через optional resolveErrorMessage)
      const errors = normalizeApiErrorItems(responseBody?.errors)
      const resolve = defaultConfig.resolveErrorMessage
      const resolveOpts = {
        errorDescription: responseBody?.error_description,
        status: response.status,
        statusText: response.statusText,
      }
      // хук → i18n; без него — legacy error_description
      // ApiBadRequest multi (несколько пунктов без loc) — склеиваем через \n
      const message = resolve
        ? errors.length > 1
          ? errors
              .map(e =>
                resolve({
                  errorCode: e.errorCode,
                  errorParams: e.errorParams,
                  ...resolveOpts,
                })
              )
              .join('\n')
          : resolve({
              errorCode: errors[0]?.errorCode,
              errorParams: errors[0]?.errorParams,
              ...resolveOpts,
            })
        : responseBody?.error_description
      throw new ApiError(responseBody?.error, message, response.status, errors)
    }

    if (noBody) {
      return
    }

    if (responseType == 'json') {
      responseBody = pick(responseBody)
      if (isPlainObject(responseBody)) {
        responseBody._loaded = true
      }
    }

    if (isReactive(model)) {
      Object.assign(model, responseBody)
    } else {
      model.value = responseBody
    }

    if (cfg.after instanceof Function) {
      cfg.after(responseBody)
    }
  }

  function abort() {
    if (!ac.value) {
      return
    }
    ac.value.abort()
    ac.value = null
    pending.value = false
  }

  return { model, error, pending, status, refresh, abort }
}

useApi.configure = setDefaultConfig

function determineBaseUrl(cfg: UseApiConfig, model: Ref): string {
  const urlPath = toValue(cfg.url)
  const method = cfg.method ?? 'get'
  const queryEntity = toValue(cfg.query)?.entity_name
  const modelEntity = toValue(model)?.entity_name

  const ssoPaths = [
    /^\/web\/(login|autologin)$/,
    /^(login|logout|current_user|sso_redirect|roles(?:\/\d+)?|users(?:\/\d+(?:\/history)?)?|users_list|user_services|users_updating(?:\/(?:request|history|yearly_test|inactive_no_prid_weekly_test))?)$/,
  ]
  const catalogsPaths = [
    /^catalog\/(medicines(?:\/\d+(?:\/history)?)?)$/,
    /^catalog\/(manufacturers(?:\/\d+(?:\/history)?)?)$/,
    /^catalog\/(medicine_trademarks(?:\/\d+(?:\/history)?)?)$/,
    /^catalog\/(medicine_trademarks(?:\/\d+(?:\/merge\/\d+)?)?)$/,
  ]
  // DropdownsCatalog живёт в catalogs: GET config/medicine_trademarks → medicinal_form и т.п.
  // (LTMS /config своих копий не отдаёт — sync DropdownsCatalog пропущен).
  const catalogsConfigPaths = [
    /^config\/(medicines|medicine_trademarks)(?:\/[^/]+)?$/,
  ]
  const configPaths = [/^config(?:\/\d+)?$/]
  const lockPaths = [/^(lock|lock_check|lock_reset)$/]
  const docparsePaths = [/^(convert)/]
  const catalogEntities = ['medicines', 'medicine_trademarks']

  if (ssoPaths.some(pattern => pattern.test(urlPath))) {
    return defaultConfig.url_sso as string
  }

  if (catalogsPaths.some(pattern => pattern.test(urlPath))) {
    return defaultConfig.url_catalog as string
  }

  if (catalogsConfigPaths.some(pattern => pattern.test(urlPath))) {
    return defaultConfig.url_catalog as string
  }

  if (configPaths.some(pattern => pattern.test(urlPath))) {
    if (['put', 'post', 'delete'].includes(method) && catalogEntities.includes(modelEntity)) {
      return defaultConfig.url_catalog as string
    }
  }

  if (lockPaths.some(pattern => pattern.test(urlPath))) {
    if (catalogEntities.includes(queryEntity)) {
      return defaultConfig.url_catalog as string
    }
  }

  if (docparsePaths.some(pattern => pattern.test(urlPath))) {
    return defaultConfig.url_docparse as string
  }

  if (urlPath.startsWith('docs:')) {
    cfg.url = urlPath.replace('docs:', '')
    return defaultConfig.url_docparse as string
  }

  return defaultConfig.url_backend as string
}

function getLockKey(url: URL, lkk: Ref) {
  const be = new URL(defaultConfig.url_backend as string)
  let uri = url.pathname.replace(be.pathname, '')

  if (uri == 'lock_check') {
    uri = `${url.searchParams.get('entity_name')}/${url.searchParams.get('id')}`
  }

  let kval = lkk?.value[uri]

  if (kval !== undefined) {
    return kval
  }

  for (const [lre, ltr] of mapUri2Entity) {
    if (!lre.test(uri)) {
      continue
    }
    const entity = ltr(uri, lre)
    kval = lkk.value[entity]
    if (!kval) {
      continue
    }
    break
  }

  return kval
}
function stripClientOnlyFields(obj) {
  if (obj == null || typeof obj !== 'object') return obj
  let o = { ...obj }
  delete o._loaded
  return o
}

const useApiResource =
  (url: string, model: Ref, refresh: RefreshFunction): ApiResourceFactory =>
  (method = null) => {
    let cfg = { url } as UseApiConfig

    const raw = toValue(model)
    const body = stripClientOnlyFields(raw)
    let _url = toValue(cfg.url)

    if (body.id) {
      _url += `/${body.id}`
    }
    // saving
    if (method === null) {
      cfg = {
        ...cfg,
        data: body,
        method: body.id ? 'put' : 'post',
      }
    } else if (method === 'delete') {
      cfg = {
        ...cfg,
        data: body,
        method: method,
      }
    } else {
      cfg.method = method
    }
    cfg.url = _url
    // cfg.immediate = false

    return refresh(cfg)
  }

// error_code / error_params / loc → shape для Form.applyApiErrors.
type ApiErrorItem = {
  errorCode: string | null
  errorParams: object
  loc: Array<string | number> | null
}

function normalizeApiErrorItems(raw: unknown): ApiErrorItem[] {
  if (!Array.isArray(raw)) {
    return []
  }
  return raw
    .filter((item): item is Record<string, unknown> => item !== null && typeof item === 'object')
    .map(item => ({
      errorCode: typeof item.error_code === 'string' ? item.error_code : null,
      errorParams:
        item.error_params && typeof item.error_params === 'object' ? item.error_params : {},
      loc: Array.isArray(item.loc) && item.loc.length ? (item.loc as Array<string | number>) : null,
    }))
}

class ApiError extends Error {
  code: number
  status: number
  // нормализованный errors[]; без 4-го аргумента — []
  errors: ApiErrorItem[]

  constructor(code: number, message: string, status: number, errors: ApiErrorItem[] = []) {
    super(message)
    this.name = 'ApiError'
    // error code from backend
    this.code = code ?? 500
    // http status
    this.status = status
    this.errors = Array.isArray(errors) ? errors : []
  }
}

export { useApi, useApiResource, useUpload, ApiError }
